/**
 * Integrations Page
 *
 * API key and webhook management for programmatic access.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState, useEffect } from 'react';
import { Plug, Key, Webhook, Boxes, CheckCircle2, XCircle, ExternalLink, Play, FileText } from 'lucide-react';
import ApiKeysTab from './ApiKeysTab';
import WebhooksTab from './WebhooksTab';
import ExplorerTab from './ExplorerTab';
import LogsTab from './LogsTab';
import { useAuth } from '../../auth';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const TABS = [
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'explorer', label: 'Explorer', icon: Play },
  { id: 'logs', label: 'Logs', icon: FileText },
  { id: 'services', label: 'Services', icon: Boxes },
];

const SERVICES = [
  {
    id: 'chainalysis',
    name: 'Chainalysis',
    description: 'Cryptocurrency wallet sanctions screening via the Free Sanctions Oracle',
    primitiveId: 'screening.wallet',
    category: 'Crypto Compliance',
    networks: ['Ethereum', 'Bitcoin', 'Polygon', 'BSC', 'Arbitrum', 'Optimism', 'Solana'],
    docsUrl: 'https://www.chainalysis.com/free-cryptocurrency-sanctions-screening-tools/',
  },
  {
    id: 'screening',
    name: 'Global Watchlists',
    description: 'AML/Sanctions/PEP screening against global watchlists',
    primitiveId: 'screening.individual',
    category: 'Compliance',
    docsUrl: null,
  },
  {
    id: 'device_intel',
    name: 'Network Intelligence',
    description: 'Device intelligence, fraud detection, VPN/proxy/TOR detection',
    primitiveId: 'device_intel.ip_check',
    category: 'Device Intelligence',
    docsUrl: null,
  },
  {
    id: 'biometrics',
    name: 'Cloud Biometrics',
    description: 'Face comparison, active liveness detection, and document OCR',
    primitiveId: 'biometrics.face_compare',
    category: 'Identity',
    docsUrl: null,
  },
  {
    id: 'mapbox',
    name: 'Mapbox',
    description: 'Address verification, geocoding, and autocomplete',
    primitiveId: 'compliance.address_verify',
    category: 'Address',
    docsUrl: 'https://www.mapbox.com/',
  },
];

function ServicesTab() {
  const { getToken } = useAuth();
  const [healthData, setHealthData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://getclearance-production.up.railway.app/api/v1';
    async function fetchHealth() {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/primitives/health`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setHealthData(data || {});
        }
      } catch (err) {
        // Silently fail - services will show as unknown
      } finally {
        setLoading(false);
      }
    }
    fetchHealth();
  }, [getToken]);

  const getStatus = (primitiveId) => {
    const health = healthData[primitiveId];
    if (!health) return 'unknown';
    if (health.is_configured) return 'configured';
    return 'not_configured';
  };

  return (
    <div className="flex flex-col gap-3">
      {SERVICES.map(service => {
        const status = getStatus(service.primitiveId);
        return (
          <div
            key={service.id}
            className="flex items-center gap-4 p-4 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-xl transition-colors hover:border-grey-300 dark:hover:border-grey-600"
          >
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-semibold text-grey-900 dark:text-grey-50 mb-1">
                {service.name}
              </h3>
              <p className="text-sm text-grey-500 dark:text-grey-400 mb-1.5 leading-snug">
                {service.description}
              </p>
              <Badge variant="secondary" className="text-[11px]">
                {service.category}
              </Badge>
              {service.networks && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {service.networks.map(n => (
                    <span
                      key={n}
                      className="px-1.5 py-0.5 bg-teal-500/10 rounded text-[10px] text-teal-500 font-medium"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap',
                status === 'configured' && 'bg-green-500/10 text-green-500',
                status === 'not_configured' && 'bg-grey-500/10 text-grey-500',
                status === 'unknown' && 'bg-grey-500/5 text-grey-400'
              )}
            >
              {status === 'configured' ? (
                <><CheckCircle2 size={14} /> Active</>
              ) : status === 'not_configured' ? (
                <><XCircle size={14} /> Not configured</>
              ) : (
                loading ? '...' : 'Unknown'
              )}
            </div>
            {service.docsUrl && (
              <a
                href={service.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-grey-400 hover:text-teal-500 transition-colors"
                title="Documentation"
              >
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function IntegrationsPage() {
  const [activeTab, setActiveTab] = useState('api-keys');

  return (
    <div className="w-full max-w-[1400px]">
      {/* Page Header */}
      <div className="flex items-start gap-3 mb-6">
        <Plug className="w-8 h-8 text-teal-500" />
        <div>
          <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
            Integrations
          </h1>
          <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
            Manage API keys, webhooks, and third-party services
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-grey-100 dark:bg-grey-800 rounded-xl mb-6 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-teal-500 text-white'
                : 'text-grey-500 dark:text-grey-400 hover:text-grey-900 dark:hover:text-grey-50 hover:bg-grey-200 dark:hover:bg-grey-700'
            )}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'api-keys' && <ApiKeysTab />}
      {activeTab === 'webhooks' && <WebhooksTab />}
      {activeTab === 'explorer' && <ExplorerTab />}
      {activeTab === 'logs' && <LogsTab />}
      {activeTab === 'services' && <ServicesTab />}
    </div>
  );
}
